import { Deserializer } from 'jsonapi-serializer';
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from '../constants/response-status-codes';
import { Translator } from '../contexts/translator';
import { BioTemplate } from '../models/BioTemplate';
import { genericError } from '../models/GenericError';
import { ListQuery } from '../models/ListQuery';
import { Name } from '../models/Name';
import { PaginationModel } from '../models/Pagination';
import { Tag, TagWrapper } from '../models/Tag';
import { getStatusId, translateCredentialDesc, translateCredentialStatus, translateCredentialType } from '../utilities/CredentialUtil';
import { convertDateToNumber } from '../utilities/DateUtils';
import { APIConnector } from './api/APIConnector';
import { convertToTime, getFormattedDate, getTime } from './utilities/dateFormatting';
import { createPagination } from './utilities/paginationBuilder';
import { getConstraintViolation, processServerError } from './utilities/ServiceError';
import { createPartialURL, getFilterClause } from './utilities/urlBuilder';
import { STANDARD_NUMBER_OF_BITS } from '../constants/credential-constants';
import { TranslationKeys } from '../contexts/TranslationKeys';

const apiConnector = new APIConnector();

const buildCredential = (credential, translate: Translator) => {
    return {
        id: credential['tag']?.['id'],
        type: translateCredentialType(credential['cred-type'], translate),
        code: credential['tag']?.['tag-display'] ?? credential['cred-code'],
        description: translateCredentialDesc(credential['cred-desc'] ?? '', translate),
        status: translateCredentialStatus(credential['cred-status'], translate),
        expiryDate: getFormattedDate(credential['tag']?.['tag-expiry'] ? credential['tag']?.['tag-expiry'] : ''),
        typeId: credential['table-type'],
        codeUntruncated: credential['tag']?.['tag-code-untruncated'],
        blacklist: credential['tag']?.['tag-blacklist'],
        suspend: credential['tag']?.['tag-suspend'],
        accessOverride: credential['tag']?.['tag-access-override'],
        guardTour: credential['tag']?.['tag-guard-tour'],
        specialEv: credential['tag']?.['tag-specialev'],
        startDate: getFormattedDate(credential['tag']?.['tag-start'] ? credential['tag']?.['tag-start'] : ''),
        startTime: convertToTime(credential['tag']?.['tag-starttime'] ? credential['tag']?.['tag-starttime'] : ''),
        expiryTime: convertToTime(credential['tag']?.['tag-expirytime'] ? credential['tag']?.['tag-expirytime'] : ''),
        statusId: getStatusId(credential['tag']?.['tag-status'], translate),
        maIdentifier: credential['tag']?.['tag-mobile-access-identifier'],
        credentials: translateCredentialType(credential['cred-credentials'], translate),
        bioTemplateId: credential['bio-template']?.['id'],
        numberOfBits: credential['tag-code-untruncated-num-bits'],
        facilityCode: credential['facility-code'],
        tagByteReversed: credential['tag']?.['tag-byte-reversed'],
        tagByteReversedLabel: credential['tag']?.['tag-byte-reversed'] === 1 ? translate.byKey(TranslationKeys.yes) : translate.byKey(TranslationKeys.no),
    };
};

const buildBioTemplete = (credential) => {return {
        id: credential['id'],
        qrCode: credential['qr-code'],
    }
}

/** Why do we need this function? We already have a buildCredential. Not touching this as yet as it's close to release */
const newBuildCredential = (credential, translate: Translator) => {
    return {
        id: credential['id'],
        type: credential['tag-type-name'],
        code: credential['tag-code'],
        description: '',
        status: translateCredentialStatus(credential['tag-status'], translate),
        expiryDate: getFormattedDate(credential['tag-expiry'] ? credential['tag-expiry'] : ''),
        typeId: credential['tag-type-untruncated']['id'].toString(),
        codeUntruncated: credential['tag-code-untruncated'],
        blacklist: credential['tag-blacklist'],
        suspend: credential['tag-suspend'],
        accessOverride: credential['tag-access-override'],
        guardTour: credential['tag-guard-tour'],
        specialEv: credential['tag-specialev'],
        startDate: getFormattedDate(credential['tag-start'] ? credential['tag-start'] : ''),
        startTime: convertToTime(credential['tag-starttime'] ? credential['tag-starttime'] : ''),
        expiryTime: convertToTime(credential['tag-expirytime'] ? credential['tag-expirytime'] : ''),
        statusId: getStatusId(credential['tag-status'], translate),
        maIdentifier: credential['tag-mobile-access-identifier'],
        credentials: translateCredentialType(credential['cred-credentials'], translate),
        numberOfBits: credential['tag-code-untruncated-num-bits'],
        facilityCode: credential['facility-code'] === "0" ? '-' : credential['facility-code'],
        truncationFormat: credential['truncation-format']?.['trn-name'] ?? '',
        credentialNumber: credential['credential-number'],
        tagDisplay: credential['tag-display'],
        tagByteReversed: credential['tag-byte-reversed'],
        tagByteReversedLabel: credential['tag-byte-reversed'] === 1 ? translate.byKey(TranslationKeys.yes) : translate.byKey(TranslationKeys.no),
    };
};

export const getMasterCredentials = async (translate: Translator,listParameters?: ListQuery): Promise<TagWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const url = `credentials?include=tag,bioTemplate${createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter)}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, apiCredentials: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const credentials = apiCredentials.map((apiCredential) => {
                    return buildCredential(apiCredential, translate);
                });

                return resolve({ rows: credentials, pagination })
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}


export const getMasterCredentialsWithPagination = async (listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, undefined);
        const url = `tag?include=master${createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter)}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, apiCredentials: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const credentials: Name[] = apiCredentials.map((apiCredential) => {
                    return { value: apiCredential['id'], label: apiCredential['tag-display'], optionalColumns: [{ key: 'tagType', value: apiCredential['tag-type-name'] }] };
                });

                return resolve({ rows: credentials, pagination })
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

/**Mobile Access credential needs to be saved as tag type 5 which is Ascii, Biometric, Magstrip */
const getRelationships = (saveState) => {
    if (saveState.truncationFormatId) {
        return {
            master: {
                data: {
                    type: 'master',
                    id: saveState.masterId
                }
            },
            tagEncodingType: {
                data: {
                    type: 'tagEncodingType',
                    id: 2
                }
            },
            tagType: {
                data: {
                    type: 'tagType',
                    id: 15
                }
            },
            tagTypeUntruncated: {
                data: {
                    type: 'tagType',
                    id: saveState.typeId ?? 1
                }
            },
            truncationFormat: {
                data: {
                    type: 'truncationFormat',
                    id: saveState.truncationFormatId
                }
            }
        }
    }

    return !saveState.maIdentifier ?
        {
            master: {
                data: {
                    type: 'master',
                    id: saveState.masterId
                }
            },
            tagEncodingType: {
                data: {
                    type: 'tagEncodingType',
                    id: 2
                }
            },
            tagType: {
                data: {
                    type: 'tagType',
                    id: 15
                }
            },
            tagTypeUntruncated: {
                data: {
                    type: 'tagType',
                    id: saveState.typeId ?? 1
                }
            }
        } :
        {
            master: {
                data: {
                    type: 'master',
                    id: saveState.masterId
                }
            },
            tagType: {
                data: {
                    type: 'tagType',
                    id: 15
                }
            },
            tagTypeUntruncated: {
                data: {
                    type: 'tagType',
                    id: 5
                }
            }
        }
}

/**
 * Function to Create/Update a tag
 * @param newCard
 * @param isCreateNew - true=POST; false=PATCH
 */
export async function saveCredential(saveState: Tag, isCreateNew = true) {
    try {
        // Always set Truncated value to the code, for creating or editing
        const truncatedVal= saveState.code;

        // For new cards or modified existing cards: New value becomes the untruncated
        // For unmodified existing cards: Maintain existing untruncated value
        const untruncatedVal = isCreateNew || saveState.isCodeModified
            ? saveState.code
            : saveState.codeUntruncated;

        const newRecord = {
            data: {
                type: 'tag',
                id: saveState.id,
                attributes: {
                    tagCode: truncatedVal,
                    tagCodeUntruncated: untruncatedVal,
                    tagSuspend: saveState.suspend,
                    tagReport: 1,
                    tagBlacklist: saveState.blacklist,
                    tagStart: saveState.startDate ? convertDateToNumber(saveState.startDate) : 0,
                    tagExpiry: saveState.expiryDate ? convertDateToNumber(saveState.expiryDate) : 0,
                    tagVisitor: 0,
                    tagSpecialev: saveState.specialEv ? saveState.specialEv : 0,
                    tagStarttime: saveState.startTime ? getTime(saveState.startTime) : "0",
                    tagExpirytime: saveState.expiryTime ? getTime(saveState.expiryTime) : "0",
                    tagOrdinal: null,
                    tagBioconfig: null,
                    tagGuardTour: saveState.guardTour,
                    tagAccessOverride: saveState.accessOverride,
                    tagDeleteOnExpire: 0,
                    tagIssueNo: 0,
                    tagIssueDate: null,
                    tagDisplay: null,
                    tagMobileAccessIdentifier: saveState.maIdentifier,
                    tagBiometric: 0,
                    tagCodeUntruncatedNumBits: saveState.numberOfBits ? saveState.numberOfBits : STANDARD_NUMBER_OF_BITS,
                    tagIsDuress: 0,
                    tagBypassFingerToc: 0,
                    tagCardWriteDate: null,
                    tagTypeChanged: true,
                    tagByteReversed: saveState.tagByteReversed ?? 0
                },
                relationships: getRelationships(saveState)
            }
        };

        const url = `tag` + (isCreateNew ? '' : '/' + newRecord.data.id);
        let response: Response;

        await apiConnector.sendData(url, isCreateNew, newRecord)
            .then(data => {
                response = data;
            }).catch(error => {
                return new Promise((resolve, reject) => {
                    return reject(processServerError(error));
                })
            });

        // Return a response to the caller: deserialized content or an error
        return new Promise(async (resolve, reject) => {
            if (response) {

                if (response.status === NO_CONTENT) {
                    return resolve(true);
                }

                const content = await response.json();

                // Errors
                if (content.errors) {
                    return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                }
                else if (isCreateNew) {
                    new Deserializer().deserialize(content, (err: any, tag: any[]) => {
                        if (err) {
                            return reject({ status: response.status, success: false, message: genericError });
                        }
                        return resolve({ content: content });
                    });
                    return;
                }

                return reject({ status: response.status, success: false, message: genericError });
            }
            else {
                return reject({ status: INTERNAL_SERVER_ERROR, success: false, message: genericError });
            }
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            return reject(processServerError(error));
        })
    }
}

export const getCredentialsTypeWithPagination = async (pagination?: PaginationModel): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const paginationString = createPartialURL(pagination?.pageNumber, pagination?.maxPageSize);
        const url = `tagType?fields[tagType]=trnName${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const types: Name[] = data.map(type => { return { label: type['trn-name'], value: type['id'] } });
                return resolve({ rows: types, pagination: newPagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        })
    }
}

export const getCredentialNameTypesWithPagination = async (listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        let filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        filter = filter.replace('ttNameOriginal', 'trnName');
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `tagType?fields[tagType]=trnName${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta)

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const tagCodeNames: Name[] = data.map(tagName => {
                    return {
                        value: tagName['id'],
                        label: tagName['trn-name'],
                    }
                })

                return resolve({ rows: tagCodeNames, pagination: pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        })
    }
}

export const getCredentialById = async (id: string, translate: Translator): Promise<Tag> => {
    try {
        const url = `tag/${id}?include=tagTypeUntruncated,truncationFormat,tagType.truncationFormat`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            new Deserializer().deserialize(json, (error: any, apiCredentials: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const credentials: Tag = newBuildCredential(apiCredentials, translate);
                return resolve(credentials)
            });

        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getBioTemplateById = async (id: string): Promise<BioTemplate> => {
    try {
        const url = `bioTemplate/${id}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            new Deserializer().deserialize(json, (error: any, apiCredentials: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const credentials: BioTemplate = buildBioTemplete(apiCredentials);
                return resolve(credentials)
            });

        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
export const deleteCredential = async (id: string): Promise<{ id: string, success: boolean }> => {
    try {
        const url = `tag/${id}`;
        const credential = {
            data: {
                type: "tag",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, credential)

        return new Promise((resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true })
            }
            return reject({ status: response.status, id: id, success: false });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        })
    }
}

export const deleteCredentialWithBioTemplate = async (id: string): Promise<{ id: string, success: boolean }> => {
    try {
        const url = `bioTemplate/${id}`;
        const credential = {
            data: {
                type: "bioTemplate",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, credential)

        return new Promise((resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true })
            }
            return reject({ status: response.status, id: id, success: false });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
